import React from 'react';
import InputField from 'components/fields/InputField';
import Radio from 'components/radio/index';
import { MerchantData, MerchantType } from '../types';

interface MerchantStepProps {
  data: MerchantData;
  onChange: (data: Partial<MerchantData>) => void;
  isProcessing?: boolean;
}

const MerchantStep: React.FC<MerchantStepProps> = ({
  data,
  onChange,
  isProcessing = false,
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    onChange({ [id]: value });
  };

  const handleTypeChange = (type: MerchantType) => {
    onChange({ type });
  };

  const handleBatchPostbackChange = (value: 'yes' | 'no') => {
    onChange({ isBatchPostback: value });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900">Merchant Information</h3>
        <p className="mt-1 text-sm text-gray-500">
          Provide the basic information for the new merchant.
        </p>
      </div>

      {/* Merchant Type Selection */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
        <label className="mb-3 block text-sm font-medium text-navy-700">
          Merchant Type <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <label
            className={`relative flex cursor-pointer rounded-xl border-2 p-4 transition-all ${
              data.type === 'regular'
                ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="merchantType"
              value="regular"
              checked={data.type === 'regular'}
              onChange={() => handleTypeChange('regular')}
              disabled={isProcessing}
              className="sr-only"
            />
            <div className="flex items-center">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  data.type === 'regular'
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <span className="block text-sm font-semibold text-gray-900">Regular Merchant</span>
                <span className="mt-1 block text-xs text-gray-500">
                  Has subaccounts with payment methods & fee groups
                </span>
              </div>
            </div>
          </label>

          <label
            className={`relative flex cursor-pointer rounded-xl border-2 p-4 transition-all ${
              data.type === 'agent'
                ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                : 'border-gray-200 bg-white hover:border-gray-300'
            }`}
          >
            <input
              type="radio"
              name="merchantType"
              value="agent"
              checked={data.type === 'agent'}
              onChange={() => handleTypeChange('agent')}
              disabled={isProcessing}
              className="sr-only"
            />
            <div className="flex items-center">
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                  data.type === 'agent'
                    ? 'bg-primary-100 text-primary-600'
                    : 'bg-gray-100 text-gray-500'
                }`}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>
              <div className="ml-4">
                <span className="block text-sm font-semibold text-gray-900">Agent Merchant</span>
                <span className="mt-1 block text-xs text-gray-500">
                  Has agents with fee group assignments
                </span>
              </div>
            </div>
          </label>
        </div>
      </div>

      {/* Basic Info */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <InputField
            disabled={isProcessing}
            variant="auth"
            extra="shadow-sm"
            label="Merchant Name"
            required={true}
            placeholder="Enter merchant name"
            id="name"
            value={data.name}
            type="text"
            autoComplete="off"
            onChange={handleInputChange}
          />
        </div>

        <div>
          <InputField
            disabled={isProcessing}
            variant="auth"
            extra="shadow-sm"
            label="Description"
            required={true}
            placeholder="Enter description"
            id="description"
            value={data.description}
            type="text"
            autoComplete="off"
            onChange={handleInputChange}
          />
        </div>

        <div>
          <InputField
            disabled={isProcessing}
            variant="auth"
            extra="shadow-sm"
            label="Support Email"
            placeholder="Enter support email"
            id="supportEmail"
            value={data.supportEmail}
            type="email"
            autoComplete="off"
            onChange={handleInputChange}
          />
        </div>

        <div>
          <InputField
            disabled={isProcessing}
            variant="auth"
            extra="shadow-sm"
            label="Restricted IPs"
            placeholder="Enter restricted IPs (comma separated)"
            id="restrictedIps"
            value={data.restrictedIps}
            type="text"
            autoComplete="off"
            onChange={handleInputChange}
            helptext="Leave empty to allow all IPs. Use comma to separate multiple IPs."
          />
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-navy-700">
            Batch Postback
          </label>
          <div className="mt-2 flex items-center gap-6">
            <label className="inline-flex items-center">
              <Radio
                id="batch_postback_yes"
                name="isBatchPostback"
                disabled={isProcessing}
                checked={data.isBatchPostback === 'yes'}
                onChange={() => handleBatchPostbackChange('yes')}
              />
              <span className="ml-2 text-sm text-navy-700">Enable</span>
            </label>
            <label className="inline-flex items-center">
              <Radio
                id="batch_postback_no"
                name="isBatchPostback"
                disabled={isProcessing}
                checked={data.isBatchPostback === 'no'}
                onChange={() => handleBatchPostbackChange('no')}
              />
              <span className="ml-2 text-sm text-navy-700">Disable</span>
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantStep;
